import { motion, useReducedMotion } from 'framer-motion'

type ReliabilityRingProps = {
  score: number | null
  size?: number
  strokeWidth?: number
  label?: string
}

/**
 * Reusable 0–100 circular gauge for the reputation/reliability tile.
 * Renders as an SVG so it scales without a raster asset. When `score` is
 * null it shows a dashed pending track.
 */
export const ReliabilityRing = ({
  score,
  size = 108,
  strokeWidth = 8,
  label = 'Reliability',
}: ReliabilityRingProps) => {
  const reduced = useReducedMotion()
  const radius = (size - strokeWidth) / 2
  const center = size / 2
  const circumference = 2 * Math.PI * radius
  const clamped = score === null ? 0 : Math.max(0, Math.min(100, score))
  const offset = circumference * (1 - clamped / 100)

  const tier =
    score === null ? 'pending' :
    clamped >= 85 ? 'legendary' :
    clamped >= 65 ? 'epic' :
    clamped >= 40 ? 'rare' : 'common'

  return (
    <div
      className={`deckRing deckRing--${tier}`}
      role="img"
      aria-label={`${label}: ${score === null ? 'pending' : `${Math.round(clamped)} out of 100`}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id="deckRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--char-accent, var(--rarity-rare))" />
            <stop offset="100%" stopColor="var(--rarity-legendary)" />
          </linearGradient>
        </defs>
        <circle
          className="deckRingTrack"
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {score !== null ? (
          <motion.circle
            className="deckRingArc"
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={strokeWidth}
            fill="none"
            stroke="url(#deckRingGradient)"
            strokeLinecap="round"
            strokeDasharray={circumference}
            transform={`rotate(-90 ${center} ${center})`}
            initial={reduced ? { strokeDashoffset: offset } : { strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: reduced ? 0 : 1.1, ease: [0.22, 1, 0.36, 1] }}
          />
        ) : null}
      </svg>
      <div className="deckRingCenter">
        {score === null ? (
          <>
            <span className="deckRingValue">—</span>
            <span className="deckRingUnit">pending</span>
          </>
        ) : (
          <>
            <span className="deckRingValue">{Math.round(clamped)}</span>
            <span className="deckRingUnit">/ 100</span>
          </>
        )}
      </div>
    </div>
  )
}
